// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** adsbSource — the LIVE ADS-B source: poll OpenSky, map each state vector to a clear-shell
 *  track envelope, and bucket each aircraft by plane type. The pure data layer behind
 *  adsbCapability (the same split as multiIntDataset behind multiIntCapability).
 *
 *  PURITY NOTE (the SDK-purity exit gate, mirrors multiIntDataset): this module imports ONLY
 *  the public-SDK envelope types (../../../../packages/contracts-ts) + its OWN bundled snapshot
 *  fixture (./adsbSnapshot.json) — NO host internal, NO abac-pdp, NO pdp.ts. adsbCapability
 *  imports this file; the SDK-purity scanner whitelists it as "its own dataset".
 *
 *  SOURCE: OpenSky Network anonymous REST (NO API KEY). The `?extended=1` form returns an 18th
 *  field — the aircraft `category` — which we bucket on; the plain form omits it (category-0),
 *  in which case we fall back to a callsign/origin heuristic. Both forms are anonymous.
 *  Anon rate limits: OpenSky throttles unauthenticated callers (≈400 credits/day, ~10s
 *  resolution). We poll on a conservative ≥20s interval and NEVER hammer; a failed/throttled
 *  poll falls back to the bundled snapshot rather than retrying tightly.
 *
 *  CLEAR-SHELL ONLY: public ADS-B is UNCLASSIFIED — every envelope is payload_mode:"clear",
 *  leakage_profile:"clear", with NO sealed/encrypted inner. Civil traffic is NEVER hostile by
 *  default; affiliation is "unknown" (the honest default for un-IFF'd civil ADS-B). */

import type { CanonicalEnvelope } from "../../../../packages/contracts-ts/src/index.ts";
import { deadReckon } from "../../../../packages/motion-model/src/motion.ts";
import ADSB_SNAPSHOT from "./adsbSnapshot.json" with { type: "json" };

/** The Hormuz AO bounding box (lat/lon) — the same box the live poll requests. */
export const HORMUZ_AO_BBOX = { lamin: 24, lomin: 51, lamax: 28, lomax: 58 } as const;

/** OpenSky anonymous REST endpoint. `extended=1` adds the `category` field (index 17) we
 *  bucket on; it is still anonymous (no key). */
export const OPENSKY_URL =
  `https://opensky-network.org/api/states/all` +
  `?lamin=${HORMUZ_AO_BBOX.lamin}&lomin=${HORMUZ_AO_BBOX.lomin}` +
  `&lamax=${HORMUZ_AO_BBOX.lamax}&lomax=${HORMUZ_AO_BBOX.lomax}&extended=1`;

/** Conservative poll interval (ms). OpenSky anon callers are credit-limited; ≥20s keeps us
 *  well under the throttle while still feeling live. Never poll faster than this. */
export const DEFAULT_POLL_MS = 30_000;
export const MIN_POLL_MS = 20_000;

/** The envelope kind ADS-B publishes (air tracks). Exported so capability + check agree. */
export const ADSB_KIND = "track" as const;

/** The human-facing plane-type buckets the headline feature surfaces (filterable in
 *  `properties.plane_bucket`). */
export type PlaneBucket =
  | "light"
  | "small"
  | "large"
  | "heavy"
  | "rotorcraft"
  | "military/special"
  | "unknown";

/** A single OpenSky state vector (extended form). Indices per the OpenSky REST contract.
 *  `category` (index 17) is present only with `extended=1`; otherwise the row has 17 fields
 *  and category is undefined ⇒ treated as 0 (no-info ⇒ heuristic fallback). */
export type OpenSkyState = [
  icao24: string,
  callsign: string | null,
  origin_country: string,
  time_position: number | null,
  last_contact: number,
  longitude: number | null,
  latitude: number | null,
  baro_altitude: number | null,
  on_ground: boolean,
  velocity: number | null,
  true_track: number | null,
  vertical_rate: number | null,
  sensors: number[] | null,
  geo_altitude: number | null,
  squawk: string | null,
  spi: boolean,
  position_source: number,
  category?: number,
];

/** The OpenSky `/states/all` response shape. */
export interface OpenSkyResponse {
  time: number;
  states: OpenSkyState[] | null;
}

/** The bundled snapshot fixture (a real OpenSky `extended=1` capture over the Hormuz AO),
 *  used for the offline/dev/test path. Typed back to the response shape. */
export const ADSB_SNAPSHOT_RESPONSE = ADSB_SNAPSHOT as unknown as OpenSkyResponse;

// ── PLANE-TYPE BUCKETING ────────────────────────────────────────────────────────────────
// OpenSky `category` codes (extended=1):
//   0 no-info · 1 no-ADS-B-info · 2 light(<15.5k lb) · 3 small(15.5–75k) · 4 large(75–300k)
//   5 high-vortex-large(B757) · 6 heavy(>300k) · 7 high-performance(>5g & >400kt)
//   8 rotorcraft · 9 glider/sailplane · 10 lighter-than-air · 11 parachutist/skydiver
//   12 ultralight/hang-glider · 13 reserved · 14 UAV · 15 space/trans-atmospheric
//   16 surface-emergency-vehicle · 17 surface-service-vehicle · 18 point-obstacle · 19 cluster · 20 line

/** Map a numeric OpenSky category to a human bucket. Returns undefined for 0/1/absent so the
 *  caller knows to apply the heuristic fallback (no-info ⇒ guess from callsign/origin). */
export function bucketFromCategory(category: number | undefined): PlaneBucket | undefined {
  switch (category) {
    case 2:
      return "light";
    case 3:
      return "small";
    case 4:
    case 5:
      return "large";
    case 6:
      return "heavy";
    case 7:
      return "military/special"; // high-performance (>5g, >400kt) — fast-jet profile
    case 8:
      return "rotorcraft";
    case 14:
    case 15:
      return "military/special"; // UAV / space / trans-atmospheric
    case 9:
    case 10:
    case 11:
    case 12:
      return "light"; // glider / LTA / parachutist / ultralight — all low-mass
    case 0:
    case 1:
    case undefined:
      return undefined; // no-info ⇒ heuristic fallback
    default:
      return "unknown"; // surface vehicles / obstacles / unknown high codes
  }
}

/** Known military/state-operator ICAO callsign prefixes (3-letter ICAO operator codes) seen
 *  over the Gulf — used by the heuristic fallback when category is 0/absent. NOT exhaustive;
 *  this only RAISES specificity, never marks affiliation (civil stays "unknown"). */
const MILITARY_CALLSIGN_PREFIXES = [
  "RCH", // Air Mobility Command (Reach)
  "CNV", // US Navy (Convoy)
  "PLF", // US (Spar/Special Air Mission adjacent)
  "RRR", // RAF (Ascot)
  "ASCOT",
  "DOOM",
  "QID", // Qatar Emiri Air Force
  "UAF", // UAE Air Force
  "IRON",
  "EVAC",
  "HOIST",
];

/** Heuristic bucket from callsign + origin when OpenSky gives no category. Conservative:
 *  it recognizes a few well-known military operator prefixes (⇒ military/special) and treats
 *  everything else as a generic airliner ⇒ "large" (the dominant Gulf traffic is wide/narrow
 *  -body airliners). Returns "unknown" only when there is nothing to go on. */
export function bucketFromHeuristics(callsign: string, originCountry: string): PlaneBucket {
  const cs = callsign.trim().toUpperCase();
  if (cs && MILITARY_CALLSIGN_PREFIXES.some((p) => cs.startsWith(p))) {
    return "military/special";
  }
  // A non-empty airline-style callsign (3 letters + flight number) ⇒ a scheduled airliner.
  // Gulf en-route traffic at altitude is overwhelmingly large transport aircraft.
  if (/^[A-Z]{3}\d/.test(cs)) return "large";
  // Has a callsign but not the airline pattern ⇒ small/GA, unless clearly nothing known.
  if (cs) return "small";
  // No callsign + no category ⇒ genuinely unknown (still a valid civil track).
  void originCountry;
  return "unknown";
}

/** OpenSky reports `origin_country` as a full English name ("United States", "United Arab
 *  Emirates"); the COP inspector's flag tile keys off a short nation CODE. Map the common
 *  Gulf-AO origin countries to a code; anything unmapped falls through to the raw country
 *  name (the inspector then shows the slate "unknown nation" tile — fail-honest). Presentation
 *  only: this RAISES the flag-tile fidelity and NEVER affects affiliation/marking. */
const COUNTRY_CODE: Record<string, string> = {
  "united states": "USA",
  "united kingdom": "GBR",
  iran: "IRN",
  "iran, islamic republic of": "IRN",
  israel: "ISR",
  china: "PRC",
  taiwan: "TWN",
  panama: "PAN",
  australia: "AUS",
  "new zealand": "NZL",
  canada: "CAN",
};

/** Map an OpenSky origin-country name → a short flag code the inspector understands; an
 *  unmapped country returns undefined (the inspector falls back to its slate tile). */
export function nationCodeFromCountry(country: string | null | undefined): string | undefined {
  if (!country) return undefined;
  return COUNTRY_CODE[country.trim().toLowerCase()];
}

/** The plane-type bucket for a state vector: category first, heuristic fallback otherwise.
 *  This is the headline derivation — surfaced in `properties.plane_bucket` so it is filterable. */
export function planeBucket(state: OpenSkyState): PlaneBucket {
  const fromCat = bucketFromCategory(state[17]);
  if (fromCat) return fromCat;
  return bucketFromHeuristics(state[1] ?? "", state[2] ?? "");
}

/** Is a state vector mappable to a track? It must have a position (lat AND lon). OpenSky can
 *  emit a state with a null position (lost track); fail-safe ⇒ DROP it rather than place a
 *  track at (0,0) (never silently normalize a malformed position). */
export function isMappable(state: OpenSkyState): boolean {
  return typeof state[5] === "number" && typeof state[6] === "number";
}

/** A stable ISO timestamp from an OpenSky epoch-seconds field. */
function isoFrom(epochSeconds: number | null | undefined, fallback: number): string {
  const secs = typeof epochSeconds === "number" ? epochSeconds : fallback;
  return new Date(secs * 1000).toISOString();
}

/** Live-track CONFIDENCE in [0,1] derived from contact RECENCY — the honest ADS-B quality
 *  signal: a freshly-heard transponder return is high-confidence; an aging one decays toward
 *  the staleness floor. Surfaced in `properties.confidence` so the host's Living-Net smear
 *  (confidence channel) + the predictive trail bite on LIVE dots too, not just synthetic
 *  scenario tracks (the gap that stopped the smear/grammar working on live ADS-B).
 *  Deterministic for a fixed (lastContact, snapshotTime) — re-publishing updates, not jitters. */
export function adsbConfidence(
  lastContact: number | null | undefined,
  snapshotTime: number,
): number {
  const last = typeof lastContact === "number" ? lastContact : snapshotTime;
  const ageSec = Math.max(0, snapshotTime - last);
  // Fresh (≤0s) → 0.92; linearly decays to the 0.55 floor over the 300s staleness window.
  const conf = 0.92 - Math.min(1, ageSec / 300) * (0.92 - 0.55);
  return +conf.toFixed(2);
}

/** Map a single OpenSky state vector → a clear-shell `track` air envelope. Civil ADS-B is
 *  UNCLASSIFIED; the affiliation is "unknown" (NEVER hostile by default). The plane bucket
 *  and the raw flight data ride in `properties` so the host can paint + filter. Deterministic
 *  for a fixed `(state, snapshotTime)` so re-publishing the same state updates (not dupes). */
export function stateToEnvelope(state: OpenSkyState, snapshotTime: number): CanonicalEnvelope {
  const icao24 = state[0];
  const callsign = (state[1] ?? "").trim();
  const origin = state[2];
  const lon = state[5] as number;
  const lat = state[6] as number;
  const baroAlt = state[7];
  const onGround = state[8];
  const velocity = state[9];
  const heading = state[10];
  const verticalRate = state[11];
  const geoAlt = state[13];
  const squawk = state[14];
  const bucket = planeBucket(state);

  // Time of this position; fall back to the snapshot time if the per-state time is absent.
  const t = isoFrom(state[3] ?? state[4], snapshotTime);
  // ADS-B tracks go stale fast (a vehicle moves); a 5-minute staleness window is generous.
  const stale = isoFrom((state[4] ?? snapshotTime) + 300, snapshotTime);
  const altitude = typeof geoAlt === "number" ? geoAlt : baroAlt;

  return {
    id: `scop:adsb:${icao24}`,
    schema: "scop.envelope/1.0",
    kind: ADSB_KIND,
    time: t,
    valid_time: { start: t, stale },
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: {
      name: callsign || icao24.toUpperCase(),
      affiliation: "unknown", // civil ADS-B — NEVER hostile by default
      type: "aircraft",
      source: "adsb:opensky",
      // ENRICHMENT (L-DATA): domain='air' + a recency-derived confidence so the live ADS-B
      // dots flow through the SAME Living-Net smear/grammar + predictive-trail path as the
      // synthetic scenario tracks (previously a gap — live dots carried no confidence/domain
      // grammar). Both additive + presentation-only; NEVER touch the marking/affiliation.
      domain: "air",
      confidence: adsbConfidence(state[4], snapshotTime),
      // The HEADLINE: the plane-type bucket, filterable.
      plane_bucket: bucket,
      icao24,
      callsign,
      origin_country: origin,
      altitude_m: altitude ?? null,
      velocity_ms: velocity ?? null,
      heading_deg: heading ?? null,
      vertical_rate_ms: verticalRate ?? null,
      on_ground: onGround,
      squawk: squawk ?? null,
      opensky_category: state[17] ?? 0,
      // ── Inspector op-grid mapping seam (additive, presentation-only) ──
      // The right-inspector op-grid + flag tile read canonical keys; expose ADS-B's
      // native telemetry under those names so live tracks populate the grid directly
      // (heading °, speed kn, altitude ft) without the inspector having to guess.
      // NEVER touches the marking/affiliation (civil ADS-B stays UNCLASSIFIED/unknown).
      heading: typeof heading === "number" ? Math.round(heading) : null,
      speed: typeof velocity === "number" ? Math.round(velocity * 1.94384) : null, // m/s → kn
      // speed_kn (knots) — the exact field name the COP's velocity leader + motion-ellipse read
      // (`speed` above is the inspector op-grid alias). Unrounded so the leader length is smooth.
      speed_kn: typeof velocity === "number" ? velocity * 1.94384 : null, // m/s → kn
      altitude: typeof altitude === "number" ? Math.round(altitude * 3.28084) : null, // m → ft
      nation: nationCodeFromCountry(origin) ?? null,
      track_number: parseInt(icao24, 16) % 9000 + 1000, // stable per-aircraft TN from ICAO24 hex
    },
    security: {
      classification: "UNCLASSIFIED",
      releasability: [],
      mission: [],
      caveats: [],
      originator_control: false,
      policy_ref: "policy://defense.local",
      payload_mode: "clear",
      leakage_profile: "clear",
    },
    provenance: {
      originator: "opensky-network",
      adapter: "adsb-opensky",
      received_at: isoFrom(snapshotTime, snapshotTime),
      chain: [{ adapter: "adsb-opensky", at: isoFrom(snapshotTime, snapshotTime) }],
    },
    payload: { clear: { callsign, origin_country: origin, plane_bucket: bucket } },
  };
}

/** Map a whole OpenSky response → the cleared-visible-agnostic set of track envelopes. DROPS
 *  un-mappable (position-less) states. Pure + deterministic. */
export function responseToEnvelopes(resp: OpenSkyResponse): CanonicalEnvelope[] {
  const states = resp.states ?? [];
  return states.filter(isMappable).map((s) => stateToEnvelope(s, resp.time));
}

/** The bundled snapshot as track envelopes (the offline/test set). */
export function snapshotEnvelopes(): CanonicalEnvelope[] {
  return responseToEnvelopes(ADSB_SNAPSHOT_RESPONSE);
}

/** m/s → knots. */
const MS_TO_KN = 1.94384;

/** Offline wrap (ms): an ADS-B track that stops refreshing (snapshot mode) loops within a
 *  bounded forward excursion instead of flying off-scope forever. 8 min @ 250 kn ≈ 33 nm. In
 *  LIVE mode a fresh poll (≤30 s) re-fixes each aircraft long before the wrap ever bites. */
export const ADSB_DEADRECKON_WRAP_MS = 8 * 60_000;

/** Advance ONE ADS-B envelope FORWARD along its heading at its velocity, `elapsedMs` since its
 *  last fix (a poll or snapshot publish), so the sky MOVES between the ≥20 s polls instead of
 *  freezing. Bounded by `periodMs` (the offline loop). Ground / position-less / static /
 *  heading-less tracks are returned UNCHANGED. Stable id ⇒ re-publishing MOVES the dot. Pure. */
export function deadReckonAdsbEnvelope(
  seed: CanonicalEnvelope,
  elapsedMs: number,
  periodMs: number = ADSB_DEADRECKON_WRAP_MS,
): CanonicalEnvelope {
  const p = seed.properties as Record<string, unknown>;
  const coords =
    seed.geometry && seed.geometry.type === "Point"
      ? (seed.geometry.coordinates as number[])
      : null;
  const headingDeg = typeof p.heading_deg === "number" ? p.heading_deg : null;
  const velMs = typeof p.velocity_ms === "number" ? p.velocity_ms : null;
  if (
    !Array.isArray(coords) ||
    coords.length < 2 ||
    headingDeg === null ||
    velMs === null ||
    velMs <= 0 ||
    p.on_ground === true
  ) {
    return seed; // nothing to advance (parked / position-less / no velocity)
  }
  const [lon, lat] = coords;
  const pos = deadReckon(lat, lon, headingDeg, velMs * MS_TO_KN, elapsedMs, periodMs);
  const moved = coords.length >= 3 ? [pos.lon, pos.lat, coords[2]] : [pos.lon, pos.lat];
  return {
    ...seed,
    geometry: { type: "Point", coordinates: moved },
    properties: { ...seed.properties, heading_deg: pos.headingDeg, heading: Math.round(pos.headingDeg) },
  };
}

/** Fetch the LIVE OpenSky picture over the Hormuz AO. Resolves to the parsed response on a
 *  200, or `null` on ANY failure (network error, non-200, throttle, malformed body) — the
 *  caller falls back to the snapshot. NEVER throws (offline-graceful). A `fetchImpl` override
 *  makes this testable without a live network. */
export async function fetchLiveStates(
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<OpenSkyResponse | null> {
  try {
    const res = await fetchImpl(OPENSKY_URL, { signal });
    if (!res.ok) return null; // 429 throttle / 5xx / 4xx ⇒ fall back, do not retry tightly
    const body = (await res.json()) as OpenSkyResponse;
    // Defensive: a 200 with a null/absent states array is a valid "no traffic" answer, but a
    // non-array states field is malformed ⇒ fail-safe to the snapshot.
    if (body.states !== null && !Array.isArray(body.states)) return null;
    return body;
  } catch {
    return null; // unreachable / DNS / timeout / parse error — caught, never thrown into host
  }
}
